import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { getDefaultCurrentData, ARI_EDITABLE_KEYS } from "./TabularViewUtils";

/**
 * Owns the grid state machine for TabularView.
 *
 * @param {object} opts
 * @param {number|string} opts.selectedRoomtype
 * @param {object}        opts.rateplan          - { value, data }
 * @param {boolean}       opts.isEditable
 * @param {Array}         opts.dateRange          - [startDate, endDate]
 */
export const useTabularViewState = ({
  selectedRoomtype,
  rateplan,
  isEditable,
  dateRange,
}) => {
  const [currentData, setCurrentData] = useState(
    getDefaultCurrentData(selectedRoomtype)
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const rateplanValue = rateplan?.value;

  // Re-seed currentData when selectedRoomtype or rateplan changes
  useEffect(() => {
    if (selectedRoomtype) {
      setCurrentData(getDefaultCurrentData(selectedRoomtype));
    }
  }, [selectedRoomtype, rateplanValue]);

  // Reset selectedRows on rateplan change
  useEffect(() => {
    setSelectedRows([]);
  }, [rateplanValue]);

  // Reset pagination when date range or roomtype changes
  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, selectedRoomtype]);

  // Derived synchronously (not via useEffect) so the Save button's disabled
  // state updates in the same render as hasPendingChanges — a post-commit
  // effect would let the button flash enabled for a frame when a row with an
  // invalid price is checked.
  const errorMessage = useMemo(
    () =>
      currentData.some((row) => {
        if (row.isCustomRow) {
          return row.price != null && row.price <= 0 && row.price !== "";
        }
        return row.price == null || row.price <= 0;
      }),
    [currentData]
  );

  // Check for Save button: currentData only contains rows the user has touched,
  // plus the bulk row (dirty once any editable field is
  // set). Deliberately stays enabled if an edit is reverted to its original
  // value — The guard is also in handleConfirm for blocking empty payloads.
  const hasPendingChanges = useMemo(
    () =>
      currentData.some((row) =>
        row.isCustomRow
          ? ARI_EDITABLE_KEYS.some(
              (key) =>
                row[key] !== null && row[key] !== undefined && row[key] !== ""
            )
          : true
      ),
    [currentData]
  );

  const handleReset = useCallback(
    (ariData) => {
      if (
        currentData.length === 0 ||
        (currentData.length === ariData.length &&
          currentData.every((val) => ariData.includes(val)))
      ) {
        toast.warn("No changes detected to reset.");
        return;
      }
      // Restore the pristine mount state (bulk row only).
      setCurrentData(getDefaultCurrentData(selectedRoomtype));
      setSelectedRows([]);
      toast.info("Data has been reset to its original state.");
    },
    [currentData, selectedRoomtype]
  );

  const handleCheckboxChange = useCallback((checked, rowData) => {
    setSelectedRows((prevSelectedRows) =>
      checked
        ? [...prevSelectedRows, rowData]
        : prevSelectedRows.filter((row) => row.date !== rowData.date)
    );

    setCurrentData((prevData) => {
      const updatedData = [...prevData];

      if (checked) {
        const rowExists = updatedData.some(
          (row) =>
            row.roomtypeId === rowData.roomtypeId && row.date === rowData.date
        );

        if (!rowExists) {
          updatedData.push({
            roomtypeId: rowData.roomtypeId,
            date: rowData.date,
            day: rowData.day,
            currency: rowData.currency,
            price: rowData.price ?? null,
            sourceTax: rowData.sourceTax ?? null,
            isOpen: rowData.isOpen ?? false,
            inventory: rowData.inventory ?? null,
            minLos: rowData.minLos ?? null,
            maxLos: rowData.maxLos ?? null,
            minLeadTime: rowData.minLeadTime ?? null,
            maxLeadTime: rowData.maxLeadTime ?? null,
          });
        }
      } else {
        return updatedData.filter(
          (row) =>
            !(
              row.roomtypeId === rowData.roomtypeId && row.date === rowData.date
            )
        );
      }

      return updatedData;
    });
  }, []);

  const handleToggleChange = useCallback(
    (checked, rowData) => {
      setCurrentData((prevData) => {
        const dummyRow = prevData.find((row) => row.isCustomRow);
        let updatedData = prevData.map((item) => {
          if (
            item?.date === rowData?.date &&
            item?.roomtypeId === rowData?.roomtypeId &&
            !item.isCustomRow
          ) {
            return { ...item, isOpen: checked };
          }
          if (item?.isCustomRow && rowData.isCustomRow) {
            return { ...item, isOpen: checked };
          }
          return item;
        });

        if (!rowData.isCustomRow) {
          const rowExists = updatedData.some(
            (item) =>
              item?.roomtypeId === rowData?.roomtypeId &&
              item?.date === rowData?.date
          );

          if (!rowExists) {
            updatedData.push({
              ...rowData,
              isOpen: checked,
            });
          }
        }

        const isDummyRowToggle =
          rowData.isCustomRow || (!rowData.date && rowData.roomtypeId);
        if (isDummyRowToggle) {
          if (!dummyRow && isEditable) {
            updatedData.push({
              roomtypeId: rowData?.roomtypeId,
              date: "unless excluded",
              day: "Applies to all rows",
              price: null,
              sourceTax: null,
              isOpen: checked,
              inventory: null,
              minLos: null,
              maxLos: null,
              minLeadTime: null,
              maxLeadTime: null,
              isCustomRow: true,
            });
          }
        } else if (dummyRow) {
          const hasMeaningfulChanges = Object.keys(dummyRow).some(
            (key) =>
              key !== "isCustomRow" &&
              dummyRow[key] !== null &&
              dummyRow[key] !== ""
          );
          if (!hasMeaningfulChanges) {
            updatedData = updatedData.filter((row) => !row.isCustomRow);
          }
        }

        return updatedData;
      });

      const isDateSelected = selectedRows.some(
        (row) => row.date === rowData.date
      );
      if (!isDateSelected) {
        setSelectedRows((prevSelectedRows) => {
          return [...prevSelectedRows, { date: rowData.date }];
        });
      }
    },
    [isEditable, selectedRows]
  );

  return {
    currentData,
    setCurrentData,
    selectedRows,
    setSelectedRows,
    errorMessage,
    hasPendingChanges,
    currentPage,
    setCurrentPage,
    handleCheckboxChange,
    handleToggleChange,
    handleReset,
  };
};
